/**
 * Digital resource connectors (Cafeyn, Mozaïk, Maxicours, Bibliovox, CVS,
 * ARTE VOD): what patrons see on the portal, the signed link that signs them
 * in, and the harvest of the provider's titles into the catalogue.
 */

import { useState } from "react";
import { AlertCircle, CheckCircle2, CircleDashed } from "lucide-react";
import { useDigitalResourcesSettings } from "@/api/hooks";
import type { DigitalResourceSettings } from "@/api/types";
import { cn } from "@/lib/utils";
import { DigitalResourceForm } from "../components/digital-resources/DigitalResourceForm";

export function DigitalResourcesSettingsPage() {
  const overview = useDigitalResourcesSettings({ pollWhileHarvesting: true });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const resources = overview.data?.resources ?? [];
  const selected = resources.find((r) => r.id === selectedId) ?? resources[0];

  return (
    <div className="space-y-6">
      <header className="max-w-3xl">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Ressources numériques
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Les ressources actives apparaissent sur le portail : un clic ouvre le service, le lecteur
          y est directement connecté. Le format du lien, le secret et l&apos;entrepôt OAI-PMH sont
          fournis par chaque fournisseur avec l&apos;abonnement.
        </p>
      </header>

      {overview.isLoading ? (
        <div
          aria-busy="true"
          aria-label="Chargement des ressources numériques"
          className="grid gap-6 lg:grid-cols-[18rem_1fr]"
        >
          <div className="h-96 animate-pulse rounded-2xl bg-gray-100 dark:bg-gray-800" />
          <div className="h-[36rem] animate-pulse rounded-2xl bg-gray-100 dark:bg-gray-800" />
        </div>
      ) : overview.error || !overview.data || !selected ? (
        <p
          role="alert"
          className="flex items-center gap-2 rounded-lg bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-300"
        >
          <AlertCircle className="h-4 w-4" aria-hidden />
          Impossible de charger les ressources numériques.
        </p>
      ) : (
        <div className="grid items-start gap-6 lg:grid-cols-[18rem_1fr]">
          <nav aria-label="Connecteurs">
            <ul className="space-y-1.5">
              {resources.map((resource) => (
                <li key={resource.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedId(resource.id);
                    }}
                    aria-current={resource.id === selected.id ? "true" : undefined}
                    className={cn(
                      "focus-visible:ring-ring w-full rounded-xl border px-4 py-3 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none",
                      resource.id === selected.id
                        ? "border-primary/50 bg-primary/5 dark:bg-primary/10"
                        : "border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
                    )}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {resource.name}
                      </span>
                      <StatusBadge resource={resource} />
                    </span>
                    <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                      {resource.category}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Keyed so that switching connector resets the form */}
          <DigitalResourceForm key={selected.id} resource={selected} overview={overview.data} />
        </div>
      )}
    </div>
  );
}

function StatusBadge({ resource }: { resource: DigitalResourceSettings }) {
  if (resource.active) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-400">
        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
        Actif
      </span>
    );
  }

  const configured = resource.access.configured || resource.harvest.configured;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
      <CircleDashed className="h-3.5 w-3.5" aria-hidden />
      {configured ? "Inactif" : "À configurer"}
    </span>
  );
}
